import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, Signal } from '@angular/core';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateModule } from '@ngx-translate/core';
import { SCREENING_CONFIG_API_SERVICE_TOKEN } from '@nk/api-lib';
import { EntityMonitoringTypeKey, PersonEntity, WithUuid } from '@nk/data-entities-lib';
import { hasPersonEntity } from '@nk/person-block';
import { ButtonDirective, EntityWithUuid, NkCardListItemAbstractComponent } from '@nk/ui-lib';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ScreeningInputParamsService } from '../../services/screening-input-params.service';
import { ScreeningEntitiesInputParams } from '../../utils/screening-entities.utils';
import { AddToScreeningModalProps } from '../../utils/screening.utils';
import { AddToScreeningModalComponent } from '../add-to-screening-modal/add-to-screening-modal.component';

@Component({
  selector: 'nk-screening-add-person-card-list-item',
  imports: [AsyncPipe, TranslateModule, ButtonDirective],
  templateUrl: './screening-add-person-card-list-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningAddPersonCardListItemComponent<Entity extends EntityWithUuid>
  extends NkCardListItemAbstractComponent<Entity>
  implements OnInit
{
  public person: Signal<WithUuid<PersonEntity> | null> = computed(() => {
    const value = this.valueSignal();
    if (value && value.entity && value.prop) {
      const item = value.entity && value.entity[value.prop];
      if (hasPersonEntity(item)) {
        return item.person;
      }
    }
    return null;
  });
  public isAddedToScreening = computed(() => {
    const value = this.valueSignal();
    if (value && value.entity && value.prop) {
      const item = value.entity && value.entity[value.prop];
      return typeof item === 'object' && item !== null && 'isAddedToScreening' in item && item.isAddedToScreening;
    }
    return false;
  });
  public entityMonitoringTypeUuid$!: Observable<string | undefined>;
  public screeningEntitiesInputParams = computed(() => {
    return this._screeningInputParamsService.screeningInputParams();
  });
  private _screeningConfigService = inject(SCREENING_CONFIG_API_SERVICE_TOKEN);
  private _screeningInputParamsService = inject(ScreeningInputParamsService);
  private _modalService = inject(BsModalService);

  public ngOnInit(): void {
    this.entityMonitoringTypeUuid$ = this._screeningConfigService.entityMonitoringTypeListCache.get().pipe(
      filter((data) => !!data),
      map(
        (data) =>
          data.find((i) => i.entity_monitoring_type_key === EntityMonitoringTypeKey.alternative_beneficial_owners)
            ?.uuid,
      ),
    );
  }

  public openAddToScreeningModal(
    person: PersonEntity,
    screeningEntitiesInputParams: ScreeningEntitiesInputParams | undefined,
    entityMonitoringTypeUuid: string,
  ): void {
    if (!(person && screeningEntitiesInputParams)) {
      return;
    }
    if (screeningEntitiesInputParams.companyUuid && screeningEntitiesInputParams.monitoringOriginEntityUuid) {
      const initialState: AddToScreeningModalProps = {
        title: extractTranslation('screening.set_on_monitoring_modal.title'),
        screeningConfigUuid: screeningEntitiesInputParams.screeningConfigUuid,
        appMembershipUuid: screeningEntitiesInputParams.appMembershipUuid,
        monitoringOriginEntityUuid: screeningEntitiesInputParams.monitoringOriginEntityUuid,
        personUuid: person?.uuid,
        entityMonitoringTypeUuid,
      };
      this._modalService.show(AddToScreeningModalComponent, {
        initialState,
      });
    }
  }
}
